import {
  Button,
  Col,
  Container,
  Modal,
  Row,
  Space,
  SpanConfig,
  Typography,
  getTypographyDefaultColor,
  useMediaQuery
} from '@ag1/ui';
import IngredientModal from '@portal/shared/modals/IngredientModal/IngredientModal';
import clsx from 'clsx';
import type { EmblaOptionsType } from 'embla-carousel';
import { useMemo, useState } from 'react';
import { useLocale } from 'shared/hooks/useLocale';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import type { UnifiedButton as UnifiedButtonType } from 'types/studio/studio';
import { SanityKeyed, ValuePropCardsV2 } from 'types/studio/studio';
import CustomPortableText from '../PortableText/PortableText';
import UnifiedButton from '../UnifiedButton/UnifiedButton';
import { CommonSectionProps } from '../types';
import styles from './ValuePropCardsV2.module.scss';
import ValuePropCardsV2Item from './ValuePropCardsV2Item/ValuePropCardsV2Item';
import ValuePropCardsV2Slider from './ValuePropCardsV2Slider/ValuePropCardsV2Slider';

type Props = Pick<ValuePropCardsV2['cards'][number], 'addLink' | 'link'> & ValuePropCardsV2;

const ValuePropCardsV2Markup = ({
  _key: key,
  _type: sectionType,
  addHeadingBlock,
  headingBlock,
  addButton,
  buttonOptions,
  baseButtonLink,
  modalOptions,
  cards,
  addSlider,
  backgroundColor,
  cardsInRow,
  theme,
  isFullWidth,
  padding,
  visibleOnPageLoad
}: SanityKeyed<Props> & CommonSectionProps) => {
  const locale = useLocale();
  const aboveSmall = useMediaQuery('s');
  const options: EmblaOptionsType = { loop: true };
  const [isOpen, setOpen] = useState(false);

  const useBottomUnifiedButton = useMemo(
    () =>
      !!(
        addButton &&
        buttonOptions === 'modal' &&
        modalOptions?.unifiedButton?.switchButton &&
        modalOptions.unifiedButton.switchButton !== 'noButton'
      ),
    [addButton, buttonOptions, modalOptions]
  );

  const colConfig = useMemo(
    () => (cardsInRow === 'two' ? { xxs: 12, s: 6 } : { xxs: 12, s: 6, l: 4 }) satisfies SpanConfig,
    [cardsInRow]
  );

  const headingButtonDesktopRight =
    headingBlock?.blockAlignment === 'left' &&
    headingBlock?.unifiedButton?.switchButton !== 'noButton' &&
    styles.headingButtonRight;

  const isBtnAtBottom = headingBlock?.isBtnAtBottom;
  const showBtnOnMobile = !aboveSmall && isBtnAtBottom;

  return (
    <div
      className={clsx(
        backgroundColor !== undefined ? styles[backgroundColor] : '',
        getThemeCSSClass(theme)
      )}>
      <Container narrow className={addSlider ? styles.noPadding : ''} id={key}>
        <div className={styles.root}>
          <Space
            top={{ xxs: 40, s: 60, m: 80, l: 100 }}
            bottom={{ xxs: 40, s: 60, m: 80, l: 100 }}
            className={clsx(getSectionPaddingClasses(styles, padding))}>
            {addHeadingBlock && (
              <div
                className={clsx(
                  styles.headingBlock,
                  headingBlock && headingBlock.blockAlignment === 'center' && styles.center,
                  addSlider && styles.padding,
                  headingButtonDesktopRight
                )}>
                {headingBlock && headingBlock.subtitle && (
                  <Typography
                    variant="subtitle"
                    size="2"
                    component="p"
                    noPadding
                    weight="700"
                    className={clsx(styles.subtitle, headingButtonDesktopRight)}
                    color={getTypographyDefaultColor(theme)}>
                    {headingBlock.subtitle}
                  </Typography>
                )}
                {headingBlock && headingBlock.superHeadline && (
                  <Typography
                    variant="subtitleMono"
                    size="2"
                    noPadding
                    color={getTypographyDefaultColor(theme)}
                    className={styles.superHeadline}>
                    {headingBlock.superHeadline}
                  </Typography>
                )}
                {headingBlock && headingBlock.headline && (
                  <Typography
                    variant="headlineSans"
                    size="2"
                    component="h2"
                    noPadding
                    className={clsx(styles.headline, headingButtonDesktopRight)}
                    color={getTypographyDefaultColor(theme)}>
                    {headingBlock.headline}
                  </Typography>
                )}
                {headingBlock && headingBlock.text && (
                  <Typography
                    variant="subtitle"
                    size="1"
                    component="div"
                    noPadding
                    className={clsx(
                      styles.text,
                      addSlider && styles.small,
                      headingBlock.isTextVariation && styles.isTextVariation,
                      headingButtonDesktopRight
                    )}
                    color={getTypographyDefaultColor(theme)}>
                    <CustomPortableText value={headingBlock.text} />
                  </Typography>
                )}
                {!showBtnOnMobile &&
                  headingBlock &&
                  headingBlock.unifiedButton &&
                  headingBlock.unifiedButton?.switchButton !== 'noButton' && (
                    <UnifiedButton
                      unifiedButton={headingBlock.unifiedButton as UnifiedButtonType}
                      sectionType={sectionType}
                      className={clsx(
                        styles.headingButton,
                        addSlider && headingBlock.blockAlignment === 'center' && styles.fullWidth,
                        headingButtonDesktopRight
                      )}
                      size={{ xxs: 'medium', l: 'large' }}
                      theme={theme}
                    />
                  )}
              </div>
            )}
            <Space top={{ s: 40, m: 48, l: 56 }}>
              <Row className={clsx(styles.wrap, addSlider && styles.hide)}>
                {cards.map((item) => (
                  <Col
                    key={item._key}
                    span={colConfig}
                    className={clsx(
                      styles.card,
                      cardsInRow === 'two' && styles.noPaddingTwo,
                      cardsInRow === 'three' && styles.noPaddingThree
                    )}>
                    <ValuePropCardsV2Item
                      sectionType={sectionType}
                      link={item.link}
                      addLink={item.addLink}
                      image={item.image}
                      portableText={item.portableText}
                      description={item.description}
                      cardsInRow={cardsInRow}
                      theme={theme}
                      isFullWidth={isFullWidth}
                      imageSizes={colConfig}
                      visibleOnPageLoad={visibleOnPageLoad}
                    />
                  </Col>
                ))}
              </Row>
              {addSlider && (
                <ValuePropCardsV2Slider
                  sectionType={sectionType}
                  options={options}
                  cards={cards}
                  addSlider={addSlider}
                  theme={theme}
                  visibleOnPageLoad={visibleOnPageLoad}
                />
              )}
            </Space>
            {addButton && (
              <Space className={clsx(styles.buttonPadding, addSlider && styles.marginTop)}>
                {useBottomUnifiedButton && (
                  <UnifiedButton
                    unifiedButton={modalOptions?.unifiedButton as UnifiedButtonType}
                    sectionType={sectionType}
                    size={{ xxs: 'medium', l: 'large' }}
                    theme={theme}
                    className={clsx(styles.btn)}
                  />
                )}
                {!useBottomUnifiedButton &&
                  buttonOptions === 'modal' &&
                  modalOptions?.baseButton && (
                    <Button
                      size="medium"
                      rounded
                      variant={modalOptions!.baseButton!.variant}
                      onClick={() => setOpen(true)}
                      icon={
                        modalOptions!.baseButton!.showIcon &&
                        modalOptions!.baseButton!.iconOptions?.icon
                      }
                      iconPosition={modalOptions!.baseButton!.iconOptions?.position}
                      className={clsx(
                        styles.btn,
                        modalOptions!.baseButton!.variant === 'outline' &&
                          backgroundColor !== 'white' &&
                          styles.transparent
                      )}
                      theme={theme}>
                      {modalOptions!.baseButton!.portableText && (
                        <CustomPortableText value={modalOptions!.baseButton!.portableText} />
                      )}
                    </Button>
                  )}
                {!useBottomUnifiedButton && buttonOptions === 'link' && baseButtonLink?.btnLink && (
                  <Button
                    size={{ xxs: 'medium', l: 'large' }}
                    rounded
                    variant={baseButtonLink.variant}
                    href={getSanityLinkUrl(baseButtonLink.btnLink, locale)}
                    icon={baseButtonLink.showIcon && baseButtonLink.iconOptions?.icon}
                    iconPosition={baseButtonLink.iconOptions?.position}
                    className={clsx(
                      baseButtonLink.variant === 'outline' &&
                        backgroundColor !== 'white' &&
                        styles.transparent
                    )}
                    theme={theme}>
                    {baseButtonLink.portableText && (
                      <CustomPortableText value={baseButtonLink.portableText} />
                    )}
                  </Button>
                )}
              </Space>
            )}

            {showBtnOnMobile &&
              headingBlock &&
              headingBlock.unifiedButton &&
              headingBlock.unifiedButton?.switchButton !== 'noButton' && (
                <UnifiedButton
                  unifiedButton={headingBlock.unifiedButton as UnifiedButtonType}
                  sectionType={sectionType}
                  className={clsx(styles.bottomButton, styles.fullWidth)}
                  size={{ xxs: 'medium', l: 'large' }}
                  theme={theme}
                />
              )}
          </Space>
        </div>
      </Container>
      <Modal
        isOpen={isOpen && !useBottomUnifiedButton}
        onClose={() => setOpen(false)}
        mode="fullScreen">
        <div className={styles.modalInner}>
          {buttonOptions === 'modal' && (
            <IngredientModal isOpen={isOpen} onClose={() => setOpen(false)} theme={theme} />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ValuePropCardsV2Markup;
